import { BackToTop, DateSelector } from 'components/common';
import { ComposableFilters } from 'components/common/ComposableFilters';
import { SegmentSelector } from 'components/common/SegmentSelector';
import { ExportIcon, ExternalLinkIcon } from 'components/icons';
import { JourneyGrid } from 'components/products/journey';
import {
  JourneyContext,
  JourneyContextProvider,
} from 'components/products/journey/JourneyContext';
import { Skeleton, SuggestionCallout } from 'components/ui';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useContext, useRef } from 'react';
import { useAppSelector } from 'store';
import { userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle } from 'utils';

const ExportMenuLazy = lazy(
  () => import('components/products/ProductsExportMenu')
);

export default function ProductJourneyPage() {
  usePageTitle('Product journey | Segments');

  const exportContainerRef = useRef<HTMLDivElement>(null);

  return (
    <JourneyContextProvider>
      <div className="max-w-screen-xl px-5 mx-auto md:pl-segments-72px md:pr-20 py-11">
        <div className="flex items-center space-x-2">
          <p className="font-bold leading-6">Products</p>
        </div>
        <h1 className="mt-4 font-bold leading-10 text-segments-44px">
          Product journey
        </h1>
        <p className="mt-6 text-base">
          Increase repeat sales by analyzing what new customers bought next.
        </p>
        <a
          href="https://help.tresl.co/en/articles/5395987-product-journey"
          className="flex items-start space-x-0.5 text-segments-link font-bold mt-2"
          target="_blank"
          rel="noreferrer"
        >
          <p>Learn more</p>
          <ExternalLinkIcon className="w-5 h-5 " />
        </a>

        <div className="relative flex items-center space-x-3 mt-7">
          <div className="w-full pl-5 pr-6 text-base border h-segments-60px bg-segments-accent-green-3 rounded-2xl border-">
            <div className="flex items-center justify-between h-full align-middle">
              <SegmentSelector />
              <ComposableFilters />
            </div>
          </div>
          <div className="2xl:flex-shrink-0">
            <DateSelector defaultPeriod="Last 365 days" />
          </div>
        </div>
        <p className="mt-1.5  text-segments-13px  text-black text-opacity-50">
          Important: the product journey has the following default settings:
          First order date.
        </p>
        <ExportHeader exportContainerRef={exportContainerRef} />
        <div className="py-2" ref={exportContainerRef}>
          <Summary />
          <div className="mt-6 font-bold text-center md:hidden">
            Module no available on mobile
          </div>
          <div className="hidden mt-10 md:block">
            <JourneyGrid />
          </div>
        </div>

        <BackToTop />
      </div>
    </JourneyContextProvider>
  );
}

function Summary() {
  const { JourneySummaryStr } = useContext(JourneyContext);

  return (
    <SuggestionCallout>
      <div className="px-6 py-4">
        <p className="text-black text-opacity-60">Summary</p>
        <p className="mt-2">{JourneySummaryStr}</p>
      </div>
    </SuggestionCallout>
  );
}

function ExportHeader({
  exportContainerRef,
}: {
  exportContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const {
    firstProduct,
    secondProduct,
    thirdProduct,
    productList,
    secondOrders,
    thirdOrders,
    journeySummary,
  } = useContext(JourneyContext);

  const { currentStore } = useAppSelector(userDetailsState);

  const numNewCustomers = journeySummary?.summary_text.match(/\d+\.?\d+/g)?.[0];

  const divide = (a: number, b: number) => {
    if (a && b && b !== 0) {
      return (a / b).toLocaleString('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return '';
  };

  const generateCSVData = useCallback(() => {
    const csvHeaders = [
      { label: 'num_new_customers', key: 'num_new_customers' },
      { label: 'first_product_name', key: 'first_product_name' },
      { label: 'first_product_customers', key: 'first_product_customers' },
      { label: 'first_product_aclv', key: 'first_product_aclv' },
      {
        label: '% of total',
        key: 'percent_of_total',
      },
      { label: 'second_product_name', key: 'second_product_name' },
      { label: 'second_product_customers', key: 'second_product_customers' },
      { label: 'second_product_avg_days', key: 'second_product_avg_days' },
      {
        label: 'second_product_most_before',
        key: 'second_product_most_before',
      },
      {
        label: 'second_product_conversion_rate',
        key: 'second_product_conversion_rate',
      },
      { label: 'third_product_name', key: 'third_product_name' },
      { label: 'third_product_customers', key: 'third_product_customers' },
      { label: 'third_product_avg_days', key: 'third_product_avg_days' },
      {
        label: 'third_product_most_before',
        key: 'third_product_most_before',
      },
      {
        label: 'third_product_conversion_rate',
        key: 'third_product_conversion_rate',
      },
    ];
    let csvData: any[] = [
      {
        num_new_customers: Number(numNewCustomers).toLocaleString(),
      },
    ];

    if (!!!firstProduct) {
      productList?.forEach((product) => {
        const row = {
          first_product_name: product?.title,
          first_product_customers: product?.unique_customers.toLocaleString(),
          first_product_aclv: product?.aclv?.toLocaleString(
            currentStore.completeInfo?.primary_locale || 'en-US',
            {
              style: 'currency',
              currency: currentStore.completeInfo?.currency || 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }
          ),
          percent_of_total: divide(
            product.unique_customers,
            Number(numNewCustomers)
          ),
        };

        csvData.push(row);
      });
    } else if (firstProduct && !!!secondProduct) {
      const row = {
        first_product_name: firstProduct?.title,
        first_product_customers:
          firstProduct?.unique_customers.toLocaleString(),
        first_product_aclv: firstProduct?.aclv?.toLocaleString(
          currentStore.completeInfo?.primary_locale || 'en-US',
          {
            style: 'currency',
            currency: currentStore.completeInfo?.currency || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }
        ),
        percent_of_total: divide(
          firstProduct.unique_customers,
          Number(numNewCustomers)
        ),
      };
      csvData.push(row);

      secondOrders?.forEach((order) => {
        const row = {
          second_product_name: order?.title,
          second_product_customers: order?.unique_customers?.toLocaleString(),
          second_product_avg_days: order?.time_lapse_mean?.toFixed(0),
          second_product_most_before: order?.time_lapse_q3?.toFixed(0),
          second_product_conversion_rate: order?.likely?.toLocaleString(
            undefined,
            {
              style: 'percent',
            }
          ),
        };
        csvData.push(row);
      });
    } else if (firstProduct && secondProduct && !!!thirdProduct) {
      const row = {
        first_product_name: firstProduct?.title,
        first_product_customers:
          firstProduct?.unique_customers.toLocaleString(),
        first_product_aclv: firstProduct?.aclv?.toLocaleString(
          currentStore.completeInfo?.primary_locale || 'en-US',
          {
            style: 'currency',
            currency: currentStore.completeInfo?.currency || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }
        ),
        percent_of_total: divide(
          firstProduct.unique_customers,
          Number(numNewCustomers)
        ),
        second_product_name: secondProduct?.title,
        second_product_customers:
          secondProduct?.unique_customers?.toLocaleString(),
        second_product_avg_days: secondProduct?.time_lapse_mean?.toFixed(0),
        second_product_most_before: secondProduct?.time_lapse_q3?.toFixed(0),
        second_product_conversion_rate: secondProduct?.likely?.toLocaleString(
          undefined,
          {
            style: 'percent',
          }
        ),
      };

      csvData.push(row);

      thirdOrders.forEach((order) => {
        const row = {
          third_product_name: order?.title,
          third_product_customers: order?.unique_customers?.toLocaleString(),
          third_product_avg_days: order?.time_lapse_mean?.toFixed(0),
          third_product_most_before: order?.time_lapse_q3?.toFixed(0),
          third_product_conversion_rate: order?.likely?.toLocaleString(
            undefined,
            {
              style: 'percent',
            }
          ),
        };
        csvData.push(row);
      });
    } else {
      const row = {
        first_product_name: firstProduct?.title,
        first_product_customers:
          firstProduct?.unique_customers.toLocaleString(),
        first_product_aclv: firstProduct?.aclv?.toLocaleString(
          currentStore.completeInfo?.primary_locale || 'en-US',
          {
            style: 'currency',
            currency: currentStore.completeInfo?.currency || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }
        ),
        percent_of_total: divide(
          firstProduct.unique_customers,
          Number(numNewCustomers)
        ),
        second_product_name: secondProduct?.title,
        second_product_customers:
          secondProduct?.unique_customers?.toLocaleString(),
        second_product_avg_days: secondProduct?.time_lapse_mean?.toFixed(0),
        second_product_most_before: secondProduct?.time_lapse_q3?.toFixed(0),
        second_product_conversion_rate: secondProduct?.likely?.toLocaleString(
          undefined,
          {
            style: 'percent',
          }
        ),
        third_product_name: thirdProduct?.title,
        third_product_customers:
          thirdProduct?.unique_customers?.toLocaleString(),
        third_product_avg_days: thirdProduct?.time_lapse_mean?.toFixed(0),
        third_product_most_before: thirdProduct?.time_lapse_q3?.toFixed(0),
        third_product_conversion_rate: thirdProduct?.likely?.toLocaleString(
          undefined,
          {
            style: 'percent',
          }
        ),
      };

      csvData.push(row);
    }

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [
    firstProduct,
    secondProduct,
    thirdProduct,
    productList,
    currentStore.completeInfo?.primary_locale,
    currentStore.completeInfo?.currency,
    secondOrders,
    thirdOrders,
    numNewCustomers,
  ]);

  const filename = `${
    currentStore.completeInfo?.store_name
  }-product_journey-${format(Date.now(), 'yyyyMMdd')}`;

  return (
    <div className="flex justify-end mt-4 md:mt-10">
      <Suspense
        fallback={
          <div className="p-3 rounded-full bg-segments-gray focus:outline-none">
            <ExportIcon className="w-5 h-5" />
          </div>
        }
      >
        <Skeleton isLoaded={Boolean(productList && productList.length > 0)}>
          <ExportMenuLazy
            containerRef={exportContainerRef}
            generateCSVData={generateCSVData}
            filename={filename}
          />
        </Skeleton>
      </Suspense>
    </div>
  );
}
